import { Search, User, Repeat2, CircleX, CalendarDays } from "lucide-react";
import DatePicker from "react-datepicker";
import React, { useEffect, useState } from "react";
import DataTablePagination from "../DataTablePagination";
import Spacing from "../Spacing";
import EasyReferralDataTable from "../DataTable/EasyReferralDataTable";
import { can_visit, exchangeDate, formatDate, getDateString, getPractice, getSpeciality, notify, numberWithCommas } from "../../helpers";
import { deleteReferral, fetchEasyReferral } from "../../services/easyReferral";
import { Link } from "react-router-dom";
import ReferralEditInformationModal from "../Modal/ReferralEditInformationModal";
import ReferralDetailsViewModal from "../Modal/ReferralDetailsViewModal";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import ImageViewModal from "../Modal/ImageViewModal";

export default function OutgoingReferral({selectedPracticeChange,fetchChangePracticeData, changePracticeSpeciality}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let specialty_id = getSpeciality()?.id || 32;
  if(selectedPracticeChange != null && fetchChangePracticeData && changePracticeSpeciality!=null){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specialty_id = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specialty_id = changePracticeSpeciality?.id || 32;
  }
  let dateStr = getDateString();

  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [viewReferralId, setViewReferralId] = useState(null);
  const [viewReferral, setViewReferral] = useState(false);
  const [editReferralData, setEditReferralData] = useState({});
  const [editReferral, setEditReferral] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [easyReferral, setEasyReferral] = useState([]);
  const [dateRange, setDateRange] = useState([dateStr, dateStr]);
  const [viewImage, setViewImage] = useState(null);
  const [openImageViewModal, setOpenImageViewModal] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    refer_type: 'outgoing',
    search_term: "",
    account_id: account_id,
    location_id: location_id,
    specialty_id: specialty_id,
    patient_first_name: "",
    patient_last_name: "",
    patient_dob: "",
    patient_sex: "",
    patient_mobile_phone: "",
    patient_insurance_ids: [],
    patient_city: "",
    patient_state: "",
    patient_zip: "",
    provider_name: "",
    practice_name: "",
    practice_mobile_phone: "",
    practice_street: "",
    practice_city: "",
    practice_state: "",
    speciality_ids: [],
    start_date: "",
    end_date: "",
    status: "",
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
    account_id: account_id,
    location_id: location_id,
    specialty_id: specialty_id
  });

  useEffect(() => {
    loadEasyReferralList(filter);
  }, [filter]);
  
  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.specialty_id === specialty_id;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      specialty_id: specialty_id
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specialty_id]);

  const loadEasyReferralList = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    if (!can_visit("easy_referral")) return
    try {
      const referralResponse = await fetchEasyReferral(filter);
      const generateData = referralResponse.data.results;
      setEasyReferral(generateData);
      setPaginationCount({
        totalCount: referralResponse.data.count,
        totalPages: referralResponse.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadEasyReferralList ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const printEasyReferral = (referraId) => {
    const url = `/easy-referral/print?referral_id=${referraId}`;
    window.open(url, '_blank');
  };

  const handleReferralChangeStatus = (e) => {
    setDataLoading(true);
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value,
    });
  };

  const handleDateChange = (update) => {
    setDataLoading(true);
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    }

    setDateRange([start, end]);
  };

  const handleDeleteRecord = async (id) => {
    try {
      const response = await deleteReferral(
        account_id,
        location_id,
        id
      );
      
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setDataLoading(true);
          notify("success", response?.data?.message);
          await loadEasyReferralList(filter);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    }
  };

  useDocumentTitle("Outgoing Referral");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_orange_bg docpro_white_color">
            <Repeat2 width={20} height={20} />
          </i>
          Outgoing referral
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>

        <div className="docpro_page_heading_btns">
          {(can_visit('easy_referral') && can_visit('easy_referral_create')) && (
            <Link
              to="/new-referral"
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn docpro_sm_btn"
            >
              <User width={20} height={20} />
              <span className="text">Refer Patient</span>
            </Link>
          )}
        </div>
      </div>
      <Spacing lg="4" sm="4" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings docpro_grid_gap_10">
          <div className="searching_div not_full_direction docpro_grid_gap_10">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className="filter_date2 not_full">
              <div
                className={`docpro_toggle_calendar ${
                  filter.start_date ? "active" : ""
                }`}
              >
                <CalendarDays />
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && filter.end_date && (
                <span onClick={() => {
                  setFilter({
                    ...filter,
                    start_date: "",
                    end_date: "",
                  });
                  setDateRange([null, null]);
                  setDataLoading(true);
                }}>
                  <CircleX size={22} color="var(--accent)" className="pointer" />
                </span>
              )}
            </div>
          </div>
          <div className="docpro_data_table_btns p-0" style={{ flexWrap: 'nowrap' }}>
            <select
              className="docpro_form_field"
              name="status"
              style={{ width: '130px', height: '36px' }}
              value={filter?.status}
              onChange={handleReferralChangeStatus}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Confirm">Confirm</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>

        <EasyReferralDataTable
          tableData={easyReferral}
          reloadData={loadEasyReferralList}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          printEasyReferral={printEasyReferral}
          setEditReferralData={setEditReferralData}
          setEditReferral={setEditReferral}
          setViewReferralId={setViewReferralId}
          setViewReferral={setViewReferral}
          handleDeleteRecord={handleDeleteRecord}
          referType="outgoing"
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(easyReferral.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {editReferral && (
        <ReferralEditInformationModal
          filter={filter}
          setDataLoading={setDataLoading}
          dataLoad={loadEasyReferralList}
          editReferral={editReferral}
          setEditReferral={setEditReferral}
          editReferralData={editReferralData}
          account_id={account_id}
          location_id={location_id}
          specialty_id={specialty_id}
          setViewImage={setViewImage}
          setOpenImageViewModal={setOpenImageViewModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}
      {viewReferral && (
        <ReferralDetailsViewModal
          filter={filter}
          setDataLoading={setDataLoading}
          dataLoad={loadEasyReferralList}
          viewReferral={viewReferral}
          setViewReferral={setViewReferral}
          viewReferralId={viewReferralId}
          account_id={account_id}
          location_id={location_id}
          specialty_id={specialty_id}
          setViewImage={setViewImage}
          setOpenImageViewModal={setOpenImageViewModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}
      {openImageViewModal && (
        <ImageViewModal
          viewImage={viewImage}
          setViewImage={setViewImage}
          activeModal={openImageViewModal}
          setActiveModal={setOpenImageViewModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
